import React from 'react';
import { Instant, Duration } from '../../utility/datetime';
export const SEGMENT_LABELS = {
    'all_customers': 'All Customers',
    'inactive_30_days': 'Customers Inactive 30+ Days',
    'due_for_service': 'Customers Due For Service',
    'recent_invoice': 'Recent Invoice Customers',
};
// Mock counts so the composer can show an estimated audience. Real
// implementation queries the customers table by segment definition.
export const SEGMENT_COUNTS = {
    'all_customers': 412,
    'inactive_30_days': 87,
    'due_for_service': 53,
    'recent_invoice': 124,
};
const TEMPLATES_KEY = 'crm_dev_email_templates';
const CAMPAIGNS_KEY = 'crm_dev_email_campaigns';
const TEMPLATES_CHANGED = 'crm-email-templates-changed';
const CAMPAIGNS_CHANGED = 'crm-email-campaigns-changed';
const nowIso = () => Instant.now().toString();
const daysAgo = (d) => Instant.now().minus(Duration.ofDays(d)).toString();
const daysFromNow = (d) => Instant.now().plus(Duration.ofDays(d)).toString();
const newId = (prefix) => `${prefix}_${Instant.now().toEpochMilli().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
const SEED_TEMPLATES = [
    {
        'id': 'tmpl_oil_change',
        'name': 'Oil Change Reminder',
        'subject': "It's time for your oil change",
        'body': 'Hi {{first_name}},\n\nIt looks like you are due for your next oil change. Reply to this email or call us to book a time that works for you.\n\nThanks for trusting us with your vehicle.',
        'created_at': daysAgo(120),
        'updated_at': daysAgo(40),
    },
    {
        'id': 'tmpl_winter_prep',
        'name': 'Winter Prep Special',
        'subject': 'Get your vehicle ready for winter',
        'body': 'Hi {{first_name}},\n\nWinter is around the corner. Book a winter prep service this month and we will check your battery, tires, and antifreeze at no charge.\n\nReply to schedule.',
        'created_at': daysAgo(95),
        'updated_at': daysAgo(95),
    },
    {
        'id': 'tmpl_thank_you',
        'name': 'Thank You After Service',
        'subject': 'Thanks for stopping in',
        'body': 'Hi {{first_name}},\n\nThanks for letting us service your vehicle. If you have any questions about the work we did, just reply to this email. We appreciate your business.',
        'created_at': daysAgo(60),
        'updated_at': daysAgo(15),
    },
];
const SEED_CAMPAIGNS = [
    {
        'id': 'camp_apr_oil',
        'name': 'April Oil Change Special',
        'template_id': 'tmpl_oil_change',
        'subject': "It's time for your oil change",
        'body': 'Hi {{first_name}},\n\nIt looks like you are due for your next oil change. Reply to this email or call us to book a time that works for you.\n\nThanks for trusting us with your vehicle.',
        'segment': 'due_for_service',
        'status': 'sent',
        'scheduled_for': null,
        'sent_at': daysAgo(7),
        'recipient_count': 53,
        'stats': { 'delivered': 50, 'opened': 21, 'clicked': 4, 'bounced': 3 },
        'created_at': daysAgo(8),
    },
    {
        'id': 'camp_winter_push',
        'name': 'Winter Prep November Push',
        'template_id': 'tmpl_winter_prep',
        'subject': 'Get your vehicle ready for winter',
        'body': 'Hi {{first_name}},\n\nWinter is around the corner. Book a winter prep service this month and we will check your battery, tires, and antifreeze at no charge.\n\nReply to schedule.',
        'segment': 'all_customers',
        'status': 'sent',
        'scheduled_for': null,
        'sent_at': daysAgo(90),
        'recipient_count': 412,
        'stats': { 'delivered': 401, 'opened': 184, 'clicked': 32, 'bounced': 11 },
        'created_at': daysAgo(91),
    },
    {
        'id': 'camp_spring_tune',
        'name': 'Spring Tune-Up Reminder',
        'template_id': null,
        'subject': 'Spring tune-up time',
        'body': 'Hi {{first_name}},\n\nIt has been a while since your last visit. Stop in this spring and we will give your car a once-over before the busy season.\n\nReply to book a time.',
        'segment': 'inactive_30_days',
        'status': 'scheduled',
        'scheduled_for': daysFromNow(3),
        'sent_at': null,
        'recipient_count': 87,
        'stats': { 'delivered': 0, 'opened': 0, 'clicked': 0, 'bounced': 0 },
        'created_at': daysAgo(1),
    },
    {
        'id': 'camp_memorial_draft',
        'name': 'Memorial Day Sale',
        'template_id': null,
        'subject': 'Memorial Day brake special',
        'body': '',
        'segment': 'all_customers',
        'status': 'draft',
        'scheduled_for': null,
        'sent_at': null,
        'recipient_count': 412,
        'stats': { 'delivered': 0, 'opened': 0, 'clicked': 0, 'bounced': 0 },
        'created_at': daysAgo(2),
    },
];
const readTemplates = () => {
    const raw = localStorage.getItem(TEMPLATES_KEY);
    if (!raw) {
        return SEED_TEMPLATES;
    }
    try {
        return JSON.parse(raw);
    }
    catch (_a) {
        return SEED_TEMPLATES;
    }
};
const writeTemplates = (ts) => {
    localStorage.setItem(TEMPLATES_KEY, JSON.stringify(ts));
    window.dispatchEvent(new CustomEvent(TEMPLATES_CHANGED));
};
const readCampaigns = () => {
    const raw = localStorage.getItem(CAMPAIGNS_KEY);
    if (!raw) {
        return SEED_CAMPAIGNS;
    }
    try {
        return JSON.parse(raw);
    }
    catch (_a) {
        return SEED_CAMPAIGNS;
    }
};
const writeCampaigns = (cs) => {
    localStorage.setItem(CAMPAIGNS_KEY, JSON.stringify(cs));
    window.dispatchEvent(new CustomEvent(CAMPAIGNS_CHANGED));
};
export const useEmailTemplates = () => {
    const [ts, setTs] = React.useState(readTemplates);
    React.useEffect(() => {
        const onChange = () => setTs(readTemplates());
        window.addEventListener(TEMPLATES_CHANGED, onChange);
        return () => window.removeEventListener(TEMPLATES_CHANGED, onChange);
    }, []);
    return [...ts].sort((a, b) => a.name.localeCompare(b.name));
};
export const useEmailCampaigns = () => {
    const [cs, setCs] = React.useState(readCampaigns);
    React.useEffect(() => {
        const onChange = () => setCs(readCampaigns());
        window.addEventListener(CAMPAIGNS_CHANGED, onChange);
        return () => window.removeEventListener(CAMPAIGNS_CHANGED, onChange);
    }, []);
    // Most recent activity first (sent_at > scheduled_for > created_at)
    return [...cs].sort((a, b) => {
        const ak = a.sent_at || a.scheduled_for || a.created_at;
        const bk = b.sent_at || b.scheduled_for || b.created_at;
        return bk.localeCompare(ak);
    });
};
export const saveTemplate = (input) => {
    const ts = readTemplates();
    const now = nowIso();
    if (input.id) {
        const existing = ts.find((t) => t.id === input.id);
        if (existing) {
            const updated = Object.assign(Object.assign({}, existing), { 'name': input.name, 'subject': input.subject, 'body': input.body, 'updated_at': now });
            writeTemplates(ts.map((t) => t.id === existing.id ? updated : t));
            return updated;
        }
    }
    const created = {
        'id': newId('tmpl'),
        'name': input.name,
        'subject': input.subject,
        'body': input.body,
        'created_at': now,
        'updated_at': now,
    };
    writeTemplates([...ts, created]);
    return created;
};
export const deleteTemplate = (id) => {
    writeTemplates(readTemplates().filter((t) => t.id !== id));
};
export const saveCampaign = (input) => {
    const cs = readCampaigns();
    const now = nowIso();
    const recipient_count = SEGMENT_COUNTS[input.segment];
    if (input.id) {
        const existing = cs.find((c) => c.id === input.id);
        if (existing) {
            const updated = Object.assign(Object.assign({}, existing), { 'name': input.name, 'template_id': input.template_id, 'subject': input.subject, 'body': input.body, 'segment': input.segment, 'status': input.status, 'scheduled_for': input.status === 'scheduled' ? (input.scheduled_for || null) : null, 'sent_at': input.status === 'sent' ? (existing.sent_at || now) : null, 'recipient_count': recipient_count });
            writeCampaigns(cs.map((c) => c.id === existing.id ? updated : c));
            return updated;
        }
    }
    const created = {
        'id': newId('camp'),
        'name': input.name,
        'template_id': input.template_id,
        'subject': input.subject,
        'body': input.body,
        'segment': input.segment,
        'status': input.status,
        'scheduled_for': input.status === 'scheduled' ? (input.scheduled_for || null) : null,
        'sent_at': input.status === 'sent' ? now : null,
        'recipient_count': recipient_count,
        'stats': { 'delivered': 0, 'opened': 0, 'clicked': 0, 'bounced': 0 },
        'created_at': now,
    };
    writeCampaigns([...cs, created]);
    return created;
};
export const deleteCampaign = (id) => {
    writeCampaigns(readCampaigns().filter((c) => c.id !== id));
};
